import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useDeleteWebhook, useWebhookWorkflowsWithRunningStatus, type PlainWebhook } from "@/queries/webhook-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { webhooksUrl, workflowName } from "@/utils/automations";
import { IconTriangleExclamation } from "central-icons/IconTriangleExclamation";
import {
    getWebhookSettingsUrl,
    scopeToDisplayString,
    WEBHOOK_PROVIDER_CONFIG,
    WEBHOOK_TYPE_CONFIG,
    isWebhookFullyConfigured,
} from "@/components/webhooks/webhook-config";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { WebhookProvider, WebhookType } from "gitpod-next-api/gitpod/v1/webhook_pb";

/**
 * The reminder is only useful when we can build a meaningful SCM settings URL,
 * which requires a scope, a known provider, and a known type.
 */
const shouldShowReminder = (webhook: PlainWebhook): boolean => {
    return (
        isWebhookFullyConfigured(webhook) &&
        webhook.spec?.provider !== WebhookProvider.UNSPECIFIED &&
        webhook.spec?.type !== WebhookType.UNSPECIFIED
    );
};

type DeleteWebhookDialogProps = {
    webhook: PlainWebhook;
    open: boolean;
    onClose: () => void;
};

export const DeleteWebhookDialog: FC<DeleteWebhookDialogProps> = ({ webhook, open, onClose }) => {
    const { toast } = useToast();
    const navigate = useNavigate();
    const deleteWebhook = useDeleteWebhook();
    const [phase, setPhase] = useState<"confirm" | "reminder">("confirm");

    const { workflows, isLoading, hasRunningExecutions, hasBoundWorkflows } = useWebhookWorkflowsWithRunningStatus(
        webhook.id,
        open && phase === "confirm",
    );

    const webhookName = webhook.metadata?.name || "Unnamed webhook";

    const handleDelete = useCallback(async () => {
        try {
            await deleteWebhook.mutateAsync(webhook.id);
            if (shouldShowReminder(webhook)) {
                setPhase("reminder");
            } else {
                toast({
                    title: "Webhook deleted",
                    description: `Webhook has been deleted successfully.`,
                });
                onClose();
                navigate(webhooksUrl());
            }
        } catch (error) {
            toast({
                title: "Failed to delete webhook",
                description: formatError(error),
            });
        }
    }, [deleteWebhook, webhook, toast, onClose, navigate]);

    const handleDone = useCallback(() => {
        setPhase("confirm");
        onClose();
        navigate(webhooksUrl());
    }, [onClose, navigate]);

    const handleOpenChange = useCallback(
        (isOpen: boolean) => {
            if (!isOpen) {
                if (phase === "reminder") {
                    handleDone();
                } else {
                    onClose();
                }
            }
        },
        [phase, handleDone, onClose],
    );

    const provider = webhook.spec?.provider;
    const webhookType = webhook.spec?.type;
    const scope = webhook.spec?.scope || "";
    const providerName = provider !== undefined ? WEBHOOK_PROVIDER_CONFIG[provider]?.name : "your SCM provider";
    const typeName = webhookType !== undefined ? WEBHOOK_TYPE_CONFIG[webhookType]?.name.toLowerCase() : "";

    // Collect all SCM configurations that need to be updated after deletion
    const settingsLinks: { label: string; url: string }[] = [];
    if (provider !== undefined && webhookType !== undefined) {
        // Organization scope
        const orgScope = webhook.spec?.organizationScope;
        if (orgScope?.name) {
            const orgUrl = getWebhookSettingsUrl(
                provider,
                WebhookType.SCM_ORGANIZATION,
                orgScope.name,
                false,
                orgScope.host,
            );
            if (orgUrl) {
                settingsLinks.push({ label: `${orgScope.name} (organization)`, url: orgUrl });
            }
        }
        // Repository scopes
        const repoScopes = webhook.spec?.scopes;
        if (repoScopes && repoScopes.length > 0) {
            for (const s of repoScopes) {
                const display = scopeToDisplayString(s);
                const repoUrl = getWebhookSettingsUrl(provider, WebhookType.SCM_REPOSITORY, display, false, s.host);
                if (repoUrl) {
                    settingsLinks.push({ label: display, url: repoUrl });
                }
            }
        }
        // Legacy scope (only if no structured scopes cover it)
        if (scope && settingsLinks.length === 0) {
            const legacyUrl = getWebhookSettingsUrl(provider, webhookType, scope);
            if (legacyUrl) {
                settingsLinks.push({ label: scope, url: legacyUrl });
            }
        }
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent data-testid="delete-webhook-dialog" data-track-location={TrackLocations.DeleteWebhookDialog}>
                {phase === "confirm" ? (
                    <>
                        <DialogHeader className="mb-0">
                            <DialogTitle>
                                {hasBoundWorkflows ? "Delete webhook" : <>Delete webhook &apos;{webhookName}&apos;</>}
                            </DialogTitle>
                            <DialogDescription>
                                This removes the webhook from Ona. It will not delete the webhook in GitHub or GitLab.
                            </DialogDescription>
                        </DialogHeader>
                        {hasBoundWorkflows && (
                            <DialogBody>
                                <div className="rounded-lg border border-border-warning px-4 py-3 text-content-primary">
                                    <div className="flex gap-3">
                                        <IconTriangleExclamation
                                            size={16}
                                            className="mt-0.5 shrink-0 text-content-warning"
                                        />
                                        <div className="flex flex-col gap-3">
                                            <p>
                                                Deleting this webhook will switch these automations to{" "}
                                                <strong>Manual</strong> and stop future PR-triggered runs.
                                            </p>
                                            {hasRunningExecutions && (
                                                <p>
                                                    Any automation that is currently executing will continue until it
                                                    completes.
                                                </p>
                                            )}
                                            <div>
                                                <p className="font-medium">
                                                    The following automations will be affected:
                                                </p>
                                                <ul className="mt-1 list-inside list-disc">
                                                    {workflows.map(({ workflow, isRunning }) => (
                                                        <li key={workflow.id}>
                                                            {workflowName(workflow)}
                                                            {isRunning && (
                                                                <span className="ml-1 italic text-content-secondary">
                                                                    - Execution running
                                                                </span>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </DialogBody>
                        )}
                        <DialogFooter className="justify-end">
                            <DialogClose asChild>
                                <Button variant="outline" data-testid="delete-webhook-dialog-cancel">
                                    Cancel
                                </Button>
                            </DialogClose>
                            <Button
                                variant="destructive"
                                onClick={handleDelete}
                                loading={deleteWebhook.isPending || isLoading}
                                disabled={isLoading}
                                data-testid="delete-webhook-dialog-confirm"
                                data-tracking-id="confirm-delete-webhook-delete-webhook-dialog"
                            >
                                Delete webhook
                            </Button>
                        </DialogFooter>
                    </>
                ) : (
                    <>
                        <DialogHeader className="mb-0">
                            <DialogTitle>Webhook deleted</DialogTitle>
                            <DialogDescription>
                                The webhook &apos;{webhookName}&apos; has been removed from Ona.
                            </DialogDescription>
                        </DialogHeader>
                        <DialogBody>
                            <div className="rounded-lg border border-border-base px-4 py-3 text-content-primary">
                                <div className="flex gap-3">
                                    <IconTriangleExclamation
                                        size={16}
                                        className="mt-0.5 shrink-0 text-content-warning"
                                    />
                                    <div className="flex flex-col gap-2">
                                        <p>
                                            Remember to also remove the webhook from your {providerName} {typeName}{" "}
                                            settings to stop sending events.
                                        </p>
                                        {settingsLinks.length > 0 && (
                                            <ul className="flex flex-col gap-1">
                                                {settingsLinks.map((link) => (
                                                    <li key={link.url}>
                                                        <ExternalLink
                                                            href={link.url}
                                                            iconSize="sm"
                                                            data-testid="delete-webhook-scm-settings-link"
                                                            data-tracking-id="open-scm-settings-delete-webhook-dialog"
                                                        >
                                                            {link.label}
                                                        </ExternalLink>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </DialogBody>
                        <DialogFooter className="justify-end">
                            <Button
                                variant="outline"
                                onClick={handleDone}
                                data-testid="delete-webhook-dialog-done"
                                data-tracking-id="done-delete-webhook-dialog"
                            >
                                Done
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};
